import { Button } from "@/components/ui/baseUi/button"
import { Checkbox } from "@/components/ui/baseUi/checkbox"
import { Separator } from "@/components/ui/baseUi/separator"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { cn } from "@/modules/lib/utils"
import { InfinityIcon } from "@images/svgIcons/infinityIcon"
import { OnOffIcon } from "@images/svgIcons/onOffIcon"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { type ProgramBundleFormPageProps } from "@modules/programBundle/components/programBundleForm"
import { UserProgramItemEntity } from "@modules/programs/entities/userProgramItemEntity"
import { usePrograms } from "@modules/programs/hooks/usePrograms"
import { ProgramFilter, ProgramStatus, ProgramType } from "@modules/programs/types/programTypes"
import { getDisplayStatus } from "@modules/programs/utils/programUtils"
import { ArrowLeft, ArrowRight } from "lucide-react"
import { Text } from "@/components/ui/baseUi/text"

export function ProgramBundleFormSecondPage({
  formik,
  getFormErrorMessage,
  handleSubmit,
  setPageNumber,
  className,
  ...props
}: ProgramBundleFormPageProps) {
  const language = useLanguage()
  const LENGTH_SHORT_TITLE = 32
  const { data: userPrograms } = usePrograms({ programFilter: ProgramFilter.OWNED })
  const typeOfFilter = formik.values.type === ProgramBundleType.VIRTUAL ? ProgramType.VIRTUAL : ProgramType.ONOFF
  const filterPrograms = userPrograms.content.filter((program) => program.type === typeOfFilter) ?? []

  const onCheckProgram = (program: UserProgramItemEntity) => {
    if ("status" in program && program.status === ProgramStatus.DRAFT) return

    if (formik.values.programIds.some((selectedProgram) => selectedProgram === program.id))
      formik.setFieldValue(
        "programIds",
        formik.values.programIds.filter((selectedProgram) => selectedProgram !== program.id),
      )
    else formik.setFieldValue("programIds", [...formik.values.programIds, program.id])
  }

  return (
    <div className={cn("flex flex-col", className)} {...props}>
      {/* TITLE */}
      <div className="flex flex-col">
        <Text fontStyle="BS" className="text-textDefault-caption">
          {word("programBundle.form.secondPage.step")}
        </Text>
        <Text fontStyle="HM" className="mt-4 text-textDefault-heading">
          {word("programBundle.form.secondPage.title")}
        </Text>
        <Text fontStyle="BM" className="mt-1 text-textDefault-heading">
          {word("programBundle.form.secondPage.subtitle")}
        </Text>
      </div>

      {/* PROGRAM TYPE */}
      <div className="mt-4 flex flex-col">
        <div className="flex justify-between">
          <div className="flex items-center gap-1">
            {formik.values.type === ProgramBundleType.VIRTUAL ? (
              <>
                <InfinityIcon
                  color="currentColor"
                  width={15}
                  height={15}
                  className="shrink-0 fill-textDefault-caption"
                />
                <Text fontStyle="LM" className="text-textDefault-caption">
                  {word("programBundle.typeOfPrograms.infitePrograms")}
                </Text>
              </>
            ) : (
              <>
                <OnOffIcon color="currentColor" width={15} height={15} className="shrink-0 fill-textDefault-caption" />
                <Text fontStyle="LM" className="text-textDefault-caption">
                  {word("programBundle.typeOfPrograms.onOffPrograms")}
                </Text>
              </>
            )}
          </div>
          <div className="flex gap-1">
            <Text fontStyle="BS" className="text-textDefault-body">
              {formik.values.programIds.length} / {filterPrograms.length}
            </Text>
            <Text fontStyle="BS" className="text-textDefault-body">
              {word("global.selected", { count: formik.values.programIds.length })}
            </Text>
          </div>
        </div>
        <Separator className="mt-2" orientation="horizontal" />
      </div>

      {/* PROGRAM LIST */}
      {filterPrograms.map((program) => {
        const isDraft = "status" in program && program.status === ProgramStatus.DRAFT
        return (
          <div className="flex flex-col" key={program.id}>
            <div className={"mt-2 flex flex-row items-center gap-3 " + (!isDraft && "cursor-pointer")}>
              <Checkbox
                size="lg"
                key={program.id}
                id={program.id}
                value={program.id}
                checked={formik.values.programIds.some((selectedProgram) => selectedProgram === program.id)}
                onCheckedChange={() => {
                  onCheckProgram(program)
                }}
              />

              <label className="flex flex-col items-start gap-1" htmlFor={program.id}>
                <div className="flex">
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Text fontStyle="TS" className="text-textDefault-heading">
                          {program.name.length > LENGTH_SHORT_TITLE
                            ? program.name.substring(0, LENGTH_SHORT_TITLE) + "..."
                            : program.name}
                        </Text>
                      }
                    />
                    {program.name.length > LENGTH_SHORT_TITLE && (
                      <TooltipContent side="top">
                        <div className="tooltip-content">
                          <Text fontStyle="BM" style={{ textAlign: "left" }}>
                            {program.name}
                          </Text>
                        </div>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </div>

                {isDraft ? (
                  <div className="items-center rounded-4xl bg-neutral-100 px-2">
                    <Text fontStyle="BS">{getDisplayStatus(program.status)}</Text>
                  </div>
                ) : program.price?.currency && program.price.value ? (
                  <Text fontStyle="BS" className="text-textDefault-body">
                    {formik.values.type === ProgramBundleType.VIRTUAL
                      ? `${formatCurrency(program.price.value, program.price.currency, language)} / ${word("global.month.label", { count: 2 })}`
                      : formatCurrency(program.price.value, program.price.currency, language)}
                  </Text>
                ) : (
                  <Text fontStyle="BS" className="text-textDefault-body">
                    {word("program.price.free")}
                  </Text>
                )}
              </label>
            </div>
            <Separator className="mt-2" orientation="horizontal" />
          </div>
        )
      })}

      {/* ERROR MESSAGE */}
      {getFormErrorMessage("programIds") && (
        <Text fontStyle="BM" className="mt-2 text-textError">
          {getFormErrorMessage("programIds")}
        </Text>
      )}

      {/* NEXT BUTTON */}
      <div className="mt-5 mb-6 flex w-full flex-row gap-5">
        <Button variant="outline" size="lg" className="w-full flex-1" onClick={() => setPageNumber((prev) => prev - 1)}>
          <ArrowLeft size={18} color="currentColor" />
          {word("global.previous")}
        </Button>

        <Button
          variant="primary"
          size="lg"
          className="w-full flex-1"
          onClick={handleSubmit}
          disabled={formik.values.programIds.length === 0}
        >
          <ArrowRight size={18} color="currentColor" />
          {word("global.next")}
        </Button>
      </div>
    </div>
  )
}
